'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ### useIsSticky
 *
 * @description
 * Reports whether an element is currently in its sticky state as the user scrolls. The element
 * must already have `position: sticky` and a `top` value in CSS; this hook does not make the
 * element sticky. Use it when you need to change styles based on stickiness—for example, adding a
 * drop shadow when stuck, or removing border radius so it aligns flush with the viewport.
 *
 * The hook reads the element's computed CSS `top` so the sticky threshold stays in sync with the
 * element's sticky `top` (e.g. from `position: sticky; top: 0`).
 *
 * @example
 * const { isSticky } = useIsSticky(ref);
 * return (
 *   <div ref={ref} className={clsx(classes.root, isSticky && classes.sticky)} />
 * );
 *
 * @param ref - Ref of the sticky element to observe.
 * @returns Object with `isSticky` true when the element is stuck, false otherwise.
 */
const useIsSticky = (ref: React.RefObject<HTMLElement | null>) => {
  const [isSticky, setIsSticky] = useState(false);
  const derivedTopRef = useRef<number | null>(null);

  const handleScroll = useCallback(() => {
    if (!ref.current) {
      derivedTopRef.current = null;
      return;
    }

    if (derivedTopRef.current === null) {
      const computedTop = getComputedStyle(ref.current).top;
      derivedTopRef.current = parseFloat(computedTop) || 0;
    }

    const effectiveOffset = derivedTopRef.current ?? 0;
    const { top } = ref.current.getBoundingClientRect();

    setIsSticky((prevIsSticky) => {
      if (prevIsSticky && top > effectiveOffset) {
        return false;
      } else if (!prevIsSticky && top <= effectiveOffset) {
        return true;
      }
      return prevIsSticky;
    });
  }, [ref]);

  useEffect(() => {
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return { isSticky };
};

export default useIsSticky;
